import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  useCallback,
  forwardRef,
} from "react";
import { LiveAvatarSession } from "@heygen/liveavatar-web-sdk";
import * as speechsdk from "microsoft-cognitiveservices-speech-sdk";
import { Room, RoomEvent } from "livekit-client";
import { AppConfig } from "../configDef";
import { setupChromaKey } from "../utils/chromaKey";
import Lottie from "lottie-react";
import powerButtonAnimation from "../Misc/PowerButtonBlue.json";
// ---------------------------------------------------------------------------
// Migration notes (HeyGen Streaming Avatar → LiveAvatar SDK)
// ---------------------------------------------------------------------------
// OLD package : @heygen/streaming-avatar
// NEW package : @heygen/liveavatar-web-sdk
//
// Key architectural changes:
//  1. Token creation: POST https://api.heygen.com/v1/streaming.create_token
//     → POST https://api.liveavatar.com/v1/sessions/token  (do this on YOUR backend)
//     The response now returns { session_token, session_id }.
//
//  2. Initialisation:
//     OLD: new StreamingAvatar({ token }) → avatar.createStartAvatar({ ... })
//     NEW: new LiveAvatarSession(sessionToken, userConfig) → session.start()
//
//  3. Speaking:
//     OLD: avatar.speak({ text })             (TaskType.REPEAT)
//     NEW: session.sendEvent("avatar.speak_text", { text })
//
//     OLD: avatar.speak({ text }) with TALK type (LLM reply)
//     NEW: session.sendEvent("avatar.speak_response", { text })
//
//  4. Interrupt:
//     OLD: avatar.interrupt()
//     NEW: session.sendEvent("avatar.interrupt")
//
//  5. Events (LiveKit room topics instead of EventEmitter):
//     "avatar.speak_started"  ← avatar started talking
//     "avatar.speak_ended"    ← avatar stopped talking
//     "session.stopped"       ← session disconnected / timed out
//     Register via: session.on("event_type", handler)
//
//  6. Teardown:
//     OLD: avatar.stopAvatar()
//     NEW: session.stop()
//
//  7. Video stream:
//     The video MediaStream is delivered the same way via an event.
//     Listen for the SDK-provided "stream_ready" event or attach the video
//     element directly through the userConfig.videoElement option.
//     See the STREAM_READY block below.
// ---------------------------------------------------------------------------

const LiveAvatarClient = forwardRef(
  ({ onAvatarReady, azureLanguage, azureVoice }, ref) => {
    const videoRef = useRef(null);
    const audioRef = useRef(null);
    const canvasRef = useRef(null);
    const [liveAvatarSession, setLiveAvatarSession] = useState(null);
    const [connected, setConnected] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);
    const [error, setError] = useState(null);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [audioBlocked, setAudioBlocked] = useState(false);
    const stopChromaKeyProcessingRef = useRef(null);
    const initRef = useRef(false); // Prevent double init in React Strict Mode
    const keepAliveIntervalRef = useRef(null);
    const sessionStateRef = useRef("connecting");
    const liteSocketRef = useRef(null);
    const livekitRoomRef = useRef(null);
    const liteReadyRef = useRef(false);
    const pendingSpeakRef = useRef(null);
    const flushPendingSpeakRef = useRef(async () => {});

    const sessionMode = String(
      AppConfig.LIVEAVATAR_MODE || "LITE",
    ).toUpperCase();
    const isLiteMode = sessionMode === "LITE";
    const resolvedAzureLanguage = azureLanguage || "en-US";

    // ---------------------------------------------------------------------------
    // Token / session-token creation
    // ---------------------------------------------------------------------------
    // IMPORTANT: In production, create the session token on your BACKEND and
    // expose it through your own API route so your LiveAvatar API key is never
    // shipped to the browser.  The helper below is a thin wrapper around the
    // LiveAvatar REST API that mirrors the old HeyGen helper for compatibility.
    // Replace `AppConfig.LIVEAVATAR_API_KEY` with the env var / config key that
    // holds your LiveAvatar API key (not the HeyGen one).
    const createSessionToken = useCallback(async () => {
      const requestBody = {
        avatar_id: AppConfig.DEFAULT_AVATAR_ID,
        mode: sessionMode,
        is_sandbox: false,
        video_settings: {
          quality: "high",
          encoding: "VP8",
        },
      };

      if (!isLiteMode) {
        requestBody.avatar_persona = {
          voice_id: AppConfig.DEFAULT_VOICE_ID,
          language: "en",
          voice_settings: {
            provider: "elevenLabs",
            speed: 1,
            stability: 0.75,
            similarity_boost: 0.75,
            style: 0,
            use_speaker_boost: true,
            model: "eleven_flash_v2_5",
          },
          stt_config: {
            provider: "deepgram",
          },
        };
      }

      const response = await fetch(
        "https://api.liveavatar.com/v1/sessions/token",
        {
          method: "POST",
          headers: {
            "X-API-KEY": AppConfig.LIVEAVATAR_API_KEY,
            "Content-Type": "application/json",
            accept: "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to create session token: ${response.status}`);
      }

      const { data } = await response.json();
      // data = { session_id: "...", session_token: "..." }
      return data.session_token;
    }, [isLiteMode, sessionMode]);

    const extractLiteConfig = (payload) => {
      if (!payload) return null;
      const data = payload.data || payload;
      const livekitConfig =
        data.livekit_config ||
        data.webrtc_config ||
        data.webrtc ||
        data.livekit ||
        {};
      const wsUrl =
        data.ws_url ||
        data.websocket_url ||
        data.wsUrl ||
        livekitConfig.websocket_url ||
        livekitConfig.ws_url ||
        null;
      const livekitUrl =
        data.livekit_url ||
        livekitConfig.url ||
        livekitConfig.livekit_url ||
        null;
      const livekitToken =
        data.livekit_client_token ||
        livekitConfig.token ||
        livekitConfig.livekit_token ||
        null;
      return {
        wsUrl,
        livekitUrl,
        livekitToken,
      };
    };

    const startLiteSession = async (sessionToken) => {
      const response = await fetch(
        "https://api.liveavatar.com/v1/sessions/start",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({}),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to start session: ${response.status}`);
      }

      return response.json();
    };

    const closeLiteConnections = () => {
      if (liteSocketRef.current) {
        liteSocketRef.current.close();
        liteSocketRef.current = null;
      }
      if (livekitRoomRef.current) {
        livekitRoomRef.current.disconnect();
        livekitRoomRef.current = null;
      }
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }
    };

    const sendLiteEvent = (payload) => {
      if (liteSocketRef.current && liteSocketRef.current.readyState === 1) {
        liteSocketRef.current.send(JSON.stringify(payload));
      }
    };

    // Called from the tap-to-enable banner or any user gesture.
    const unlockAudioOnGesture = useCallback(() => {
      if (audioRef.current) {
        audioRef.current.muted = false;
        void audioRef.current.play().catch(() => {});
      }
      setAudioBlocked(false);
    }, []);

    const playAudioElement = useCallback(async () => {
      if (!audioRef.current) {
        return;
      }

      try {
        await audioRef.current.play();
      } catch (playError) {
        console.warn("[DEBUG] Audio element play failed", playError);
      }
    }, []);

    // ---------------------------------------------------------------------------
    // Chroma key helpers
    // ---------------------------------------------------------------------------
    const setupChromaKeyProcessing = useCallback(() => {
      if (!videoRef.current || !canvasRef.current) return;

      if (stopChromaKeyProcessingRef.current) {
        stopChromaKeyProcessingRef.current();
        stopChromaKeyProcessingRef.current = null;
      }

      canvasRef.current.style.display = "block";
      videoRef.current.style.display = "block";
      videoRef.current.style.opacity = "0";

      stopChromaKeyProcessingRef.current = setupChromaKey(
        videoRef.current,
        canvasRef.current,
        {
          minHue: 90,
          maxHue: 150,
          minSaturation: 0.15,
          threshold: 1.02,
        },
      );
    }, []);

    // ---------------------------------------------------------------------------
    // Main init
    // ---------------------------------------------------------------------------
    const createAndInitializeAvatar = useCallback(async () => {
      try {
        console.log("[DEBUG] Initialising LiveAvatar session…");
        setIsInitializing(true);
        setError(null);
        setSessionExpired(false);
        liteReadyRef.current = false;
        pendingSpeakRef.current = null;

        // 1. Obtain a short-lived session token (ideally from your backend)
        console.log("[DEBUG] Fetching session token");
        const sessionToken = await createSessionToken();
        console.log("[DEBUG] Session token received", sessionToken);

        if (isLiteMode) {
          const startResp = await startLiteSession(sessionToken);
          const liteConfig = extractLiteConfig(startResp);
          if (!liteConfig?.livekitUrl || !liteConfig?.livekitToken) {
            throw new Error(
              "Missing LiveKit connection info in session start response",
            );
          }
          if (!liteConfig?.wsUrl) {
            throw new Error("Missing WebSocket URL in session start response");
          }

          closeLiteConnections();

          const room = new Room({
            adaptiveStream: true,
            dynacast: true,
          });
          await room.connect(liteConfig.livekitUrl, liteConfig.livekitToken, {
            autoSubscribe: true,
          });
          livekitRoomRef.current = room;

          console.log("[DEBUG] LiveKit connected", {
            name: room.name,
            state: room.state,
            participants: room.remoteParticipants?.size ?? 0,
            localIdentity: room.localParticipant?.identity,
          });

          const attachTrack = (track) => {
            if (track.kind === "video") {
              if (videoRef.current) {
                track.attach(videoRef.current);
                videoRef.current.muted = true;
                videoRef.current.onloadedmetadata = () => {
                  videoRef.current.play().then(() => {
                    if (canvasRef.current && isLiteMode) {
                      canvasRef.current.style.display = "block";
                    } else if (canvasRef.current) {
                      canvasRef.current.style.display = "block";
                    }
                    videoRef.current.style.display = "block";
                    if (isLiteMode) {
                      videoRef.current.style.opacity = "0";
                    }
                    console.log("[DEBUG] video element is playing and visible");
                  });
                };
              } else {
                const videoEl = track.attach();
                videoEl.muted = true;
                document.body.appendChild(videoEl);
              }
            }
            if (track.kind === "audio") {
              const audioEl = audioRef.current || track.attach();
              track.attach(audioEl);
              audioEl.autoplay = true;
              audioEl.playsInline = true;
              audioEl.muted = false;
              audioEl
                .play()
                .then(() => {
                  setAudioBlocked(false);
                })
                .catch(() => {
                  // Browser blocked unmuted autoplay – fall back to muted and
                  // show the tap-to-enable banner so the next gesture unlocks it.
                  audioEl.muted = true;
                  setAudioBlocked(true);
                  void audioEl.play().catch(() => {});
                });
            }
            setConnected(true);
            if (!liteReadyRef.current) {
              liteReadyRef.current = true;
              if (onAvatarReady && typeof onAvatarReady === "function") {
                onAvatarReady(null);
              }
            }
          };

          const bindPublication = (participant, publication) => {
            console.log("[DEBUG] LiveKit publication", {
              identity: participant.identity,
              kind: publication.kind,
              source: publication.source,
              isSubscribed: publication.isSubscribed,
            });
            if (!publication.isSubscribed) {
              publication.setSubscribed(true);
            }
            if (publication.track) {
              attachTrack(publication.track);
            }
            if (typeof publication.on === "function") {
              publication.on("subscribed", (track) => {
                attachTrack(track);
              });
              publication.on("subscriptionStatusChanged", (status) => {
                console.log("[DEBUG] LiveKit subscription status", {
                  identity: participant.identity,
                  kind: publication.kind,
                  status,
                });
              });
            }
          };

          const subscribeExistingTracks = () => {
            room.remoteParticipants?.forEach((participant) => {
              participant.trackPublications.forEach((publication) => {
                bindPublication(participant, publication);
              });
            });
          };

          setTimeout(() => {
            console.log("[DEBUG] LiveKit snapshot", {
              state: room.state,
              participants: room.remoteParticipants?.size ?? 0,
              localIdentity: room.localParticipant?.identity,
            });
            room.remoteParticipants?.forEach((participant) => {
              console.log("[DEBUG] Remote participant", {
                identity: participant.identity,
                tracks: participant.trackPublications.size,
              });
            });
          }, 5000);

          room.on(RoomEvent.ParticipantConnected, (participant) => {
            console.log("[DEBUG] LiveKit participant connected", {
              identity: participant.identity,
              isRemote: participant.isRemote,
              tracks: participant.trackPublications.size,
            });
            participant.trackPublications.forEach((publication) => {
              bindPublication(participant, publication);
            });
          });

          room.on(RoomEvent.ParticipantDisconnected, (participant) => {
            console.log("[DEBUG] LiveKit participant disconnected", {
              identity: participant.identity,
            });
          });

          room.on(RoomEvent.TrackPublished, (publication, participant) => {
            if (participant && participant.isRemote) {
              console.log("[DEBUG] LiveKit track published", {
                kind: publication.kind,
                source: publication.source,
                isSubscribed: publication.isSubscribed,
                identity: participant.identity,
              });
              bindPublication(participant, publication);
            }
          });

          room.on(RoomEvent.TrackSubscribed, (track) => {
            console.log("[DEBUG] LiveKit track subscribed", {
              kind: track.kind,
            });
            attachTrack(track);
          });

          subscribeExistingTracks();

          setTimeout(() => {
            console.log("[DEBUG] LiveKit resubscribe sweep");
            subscribeExistingTracks();
          }, 3000);

          const socket = new WebSocket(liteConfig.wsUrl);
          liteSocketRef.current = socket;

          socket.onopen = () => {
            console.log("[DEBUG] Lite socket connected");
            sessionStateRef.current = "connected";
            setConnected(true);
            void flushPendingSpeakRef.current();
            if (!liteReadyRef.current) {
              liteReadyRef.current = true;
              if (onAvatarReady && typeof onAvatarReady === "function") {
                onAvatarReady(null);
              }
            }
          };
          socket.onmessage = (event) => {
            try {
              const message = JSON.parse(event.data);
              const eventType =
                message.type || message.event || message.event_type;
              if (eventType === "session.state_updated") {
                sessionStateRef.current = message.state || "connected";
                setConnected(sessionStateRef.current === "connected");
                if (sessionStateRef.current === "connected") {
                  void flushPendingSpeakRef.current();
                }
              }
              if (eventType === "agent.speak_started") {
                setIsSpeaking(true);
              }
              if (eventType === "agent.speak_ended") {
                setIsSpeaking(false);
              }
            } catch (parseErr) {
              console.warn(
                "[DEBUG] Failed to parse lite socket message",
                parseErr,
              );
            }
          };
          socket.onerror = (socketErr) => {
            console.error("[DEBUG] Lite socket error", socketErr);
          };
          socket.onclose = () => {
            console.log("[DEBUG] Lite socket closed");
            sessionStateRef.current = "closed";
            setConnected(false);
            setSessionExpired(true);
            if (keepAliveIntervalRef.current) {
              clearInterval(keepAliveIntervalRef.current);
              keepAliveIntervalRef.current = null;
            }
          };

          if (keepAliveIntervalRef.current) {
            clearInterval(keepAliveIntervalRef.current);
          }
          keepAliveIntervalRef.current = setInterval(() => {
            sendLiteEvent({
              type: "session.keep_alive",
              event_id: `${Date.now()}`,
            });
          }, 240000);

          return null;
        }

        // 2. Create the LiveAvatarSession
        const userConfig = {
          // voiceChat: true, // enable bi-directional voice conversation (FULL mode)
          // videoElement: videoRef.current, // optional – SDK can attach the stream itself
        };
        console.log("[DEBUG] Creating LiveAvatarSession");
        const session = new LiveAvatarSession(sessionToken, userConfig);
        await session.start();
        console.log("[DEBUG] LiveAvatar session started");

        setLiveAvatarSession(session);

        // 3. Wire up events
        session.on("avatar.speak_started", () => {
          console.log("[DEBUG] avatar.speak_started event");
          setIsSpeaking(true);
        });

        session.on("avatar.speak_ended", () => {
          console.log("[DEBUG] avatar.speak_ended event");
          setIsSpeaking(false);
        });

        session.on("session.stopped", (evt) => {
          console.log("[DEBUG] session.stopped event", evt);
          sessionStateRef.current = "closed";
          setConnected(false);
          setSessionExpired(true);
          setLiveAvatarSession(null);
          if (keepAliveIntervalRef.current) {
            clearInterval(keepAliveIntervalRef.current);
            keepAliveIntervalRef.current = null;
          }
        });

        session.on("stream_ready", (mediaStream) => {
          console.log("[DEBUG] stream_ready event fired", { mediaStream });
          if (!videoRef.current) {
            console.warn(
              "[DEBUG] videoRef.current is null when stream_ready fired",
            );
          }
          if (!mediaStream) {
            console.warn("[DEBUG] mediaStream is null in stream_ready event");
          }
          if (videoRef.current && mediaStream) {
            videoRef.current.srcObject = mediaStream;
            videoRef.current.muted = true;
            videoRef.current.onloadedmetadata = () => {
              videoRef.current.play().then(() => {
                if (canvasRef.current) {
                  canvasRef.current.style.display = "block";
                }
                videoRef.current.style.display = "block";
                console.log("[DEBUG] video element is playing and visible");
              });
            };
          }
          if (audioRef.current) {
            audioRef.current.srcObject = mediaStream;
            audioRef.current.autoplay = true;
            audioRef.current.muted = false;
            audioRef.current
              .play()
              .then(() => {
                setAudioBlocked(false);
              })
              .catch(() => {
                audioRef.current.muted = true;
                setAudioBlocked(true);
                void audioRef.current.play().catch(() => {});
              });
          }
          setConnected(true);

          if (onAvatarReady && typeof onAvatarReady === "function") {
            onAvatarReady(session);
          }
        });

        session.on("error", (err) => {
          console.error("[DEBUG] LiveAvatar session error event", err);
        });

        // 4. Start the session (replaces avatar.createStartAvatar())

        return session;
      } catch (err) {
        console.error("[DEBUG] Error initialising LiveAvatar:", err);

        if (err.message?.includes("Concurrent limit reached")) {
          setError("Concurrent limit reached. Please wait and try again.");
        } else {
          setError(err.message || "Failed to initialise avatar");
        }

        setConnected(false);
        return null;
      } finally {
        setIsInitializing(false);
      }
    }, [createSessionToken, isLiteMode, onAvatarReady]);

    // ---------------------------------------------------------------------------
    // Mount / unmount
    // ---------------------------------------------------------------------------
    useEffect(() => {
      if (initRef.current) return;
      initRef.current = true;

      let currentSession = null;

      const init = async () => {
        currentSession = await createAndInitializeAvatar();
      };
      init();

      return () => {
        if (currentSession) {
          console.log("Cleaning up LiveAvatar session on unmount");
          currentSession.stop(); // replaces stopAvatar()
        }
        closeLiteConnections();
        if (keepAliveIntervalRef.current) {
          clearInterval(keepAliveIntervalRef.current);
          keepAliveIntervalRef.current = null;
        }
        if (stopChromaKeyProcessingRef.current) {
          stopChromaKeyProcessingRef.current();
          stopChromaKeyProcessingRef.current = null;
        }
      };
    }, [createAndInitializeAvatar]);

    useEffect(() => {
      const handleUserInteraction = () => {
        unlockAudioOnGesture();
        void playAudioElement();
      };

      window.addEventListener("pointerdown", handleUserInteraction, {
        passive: true,
      });
      window.addEventListener("touchstart", handleUserInteraction, {
        passive: true,
      });

      return () => {
        window.removeEventListener("pointerdown", handleUserInteraction);
        window.removeEventListener("touchstart", handleUserInteraction);
      };
    }, [unlockAudioOnGesture, playAudioElement]);

    // Re-apply chroma key whenever the stream comes in
    useEffect(() => {
      if (isLiteMode && connected && videoRef.current && canvasRef.current) {
        setupChromaKeyProcessing();
      }
    }, [connected, isLiteMode, setupChromaKeyProcessing]);

    // ---------------------------------------------------------------------------
    // Restart helper
    // ---------------------------------------------------------------------------
    const restartSession = async () => {
      console.log("Restarting LiveAvatar session…");

      if (liveAvatarSession) {
        liveAvatarSession.stop();
        setLiveAvatarSession(null);
      }
      closeLiteConnections();
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }
      // if (stopChromaKeyProcessingRef.current) {
      //   stopChromaKeyProcessingRef.current();
      //   stopChromaKeyProcessingRef.current = null;
      // }

      await createAndInitializeAvatar();
    };

    // ---------------------------------------------------------------------------
    // Imperative handle (public API exposed to parent via ref)
    // ---------------------------------------------------------------------------
    const uint8ToBase64 = (bytes) => {
      let binary = "";
      const chunkSize = 0x8000;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
      }
      return btoa(binary);
    };

    const synthesizeTextToPcm = async (text) => {
      if (!AppConfig.AZURE_SPEECH_KEY || !AppConfig.AZURE_SPEECH_REGION) {
        throw new Error("Azure TTS is not configured");
      }

      const speechConfig = speechsdk.SpeechConfig.fromSubscription(
        AppConfig.AZURE_SPEECH_KEY,
        AppConfig.AZURE_SPEECH_REGION,
      );
      speechConfig.speechSynthesisLanguage = resolvedAzureLanguage;
      if (azureVoice) {
        speechConfig.speechSynthesisVoiceName = azureVoice;
      }
      speechConfig.speechSynthesisOutputFormat =
        speechsdk.SpeechSynthesisOutputFormat.Raw24Khz16BitMonoPcm;

      const synthesizer = new speechsdk.SpeechSynthesizer(speechConfig);

      return new Promise((resolve, reject) => {
        synthesizer.speakTextAsync(
          text,
          (result) => {
            synthesizer.close();
            if (
              result.reason ===
              speechsdk.ResultReason.SynthesizingAudioCompleted
            ) {
              resolve(result.audioData);
            } else {
              reject(new Error(result.errorDetails || "TTS failed"));
            }
          },
          (err) => {
            synthesizer.close();
            reject(err);
          },
        );
      });
    };

    const sendPcmAudioBuffer = async (audioBuffer) => {
      if (!liveAvatarSession && !isLiteMode) return;
      if (isLiteMode && !liteSocketRef.current) return;
      const bytes = new Uint8Array(audioBuffer);
      const root =
        (typeof window !== "undefined" && window) ||
        (typeof global !== "undefined" && global) ||
        {};
      const eventId = root?.crypto?.randomUUID?.() || `evt_${Date.now()}`;
      const chunkSize = 48000; // ~1 second at 24kHz 16-bit mono

      for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize);
        const audio = uint8ToBase64(chunk);
        if (isLiteMode) {
          sendLiteEvent({
            type: "agent.speak",
            event_id: eventId,
            audio,
          });
        } else {
          liveAvatarSession.sendEvent("agent.speak", {
            type: "agent.speak",
            event_id: eventId,
            audio,
          });
        }
      }

      if (isLiteMode) {
        sendLiteEvent({
          type: "agent.speak_end",
          event_id: eventId,
        });
      } else {
        liveAvatarSession.sendEvent("agent.speak_end", {
          type: "agent.speak_end",
          event_id: eventId,
        });
      }
    };

    useImperativeHandle(ref, () => ({
      // Speak text – maps to the old avatar.speak({ text, task_type: REPEAT })
      speak: async (text) => {
        if ((!liveAvatarSession && !isLiteMode) || !connected) {
          console.warn("Avatar not ready yet");
          return;
        }
        if (isMuted) {
          console.warn("Avatar is muted");
          return;
        }
        try {
          if (isLiteMode) {
            if (sessionStateRef.current !== "connected") {
              console.warn("Session not connected yet, queuing speech");
              pendingSpeakRef.current = text;
              return;
            }
            const audioData = await synthesizeTextToPcm(text);
            await sendPcmAudioBuffer(audioData);
          } else {
            // "avatar.speak_text" → avatar speaks the exact text (replaces TaskType.REPEAT)
            liveAvatarSession.sendEvent("avatar.speak_text", { text });
          }
          setIsMuted(false);
        } catch (err) {
          console.error("[DEBUG] speak error", err);
          setError(err?.message || "Failed to synthesize speech");
        }
      },

      // Repeat is identical to speak in LiveAvatar (both use speak_text)
      repeat: async (text) => {
        if ((!liveAvatarSession && !isLiteMode) || !connected) {
          console.warn("Avatar not ready yet");
          return;
        }
        if (isMuted) {
          console.warn("Avatar is muted");
          return;
        }
        try {
          if (isLiteMode) {
            if (sessionStateRef.current !== "connected") {
              console.warn("Session not connected yet, queuing speech");
              pendingSpeakRef.current = text;
              return;
            }
            const audioData = await synthesizeTextToPcm(text);
            await sendPcmAudioBuffer(audioData);
          } else {
            liveAvatarSession.sendEvent("avatar.speak_text", { text });
          }
          setIsMuted(false);
        } catch (err) {
          console.error("[DEBUG] repeat error", err);
          setError(err?.message || "Failed to synthesize speech");
        }
      },

      // If you want the avatar to generate an LLM reply, use this instead:
      // chat: (text) => {
      //   liveAvatarSession?.sendEvent("avatar.speak_response", { text });
      // },

      // Interrupt – replaces avatar.interrupt()
      interrupt: () => {
        if (isLiteMode) {
          sendLiteEvent({ type: "agent.interrupt" });
          setIsMuted(true);
          return;
        }
        if (liveAvatarSession) {
          liveAvatarSession.sendEvent("avatar.interrupt");
          setIsMuted(true);
        }
      },

      isReady: () => connected,
      isMuted: () => isMuted,
      setMuted: (muted) => setIsMuted(muted),

      // Chroma key is always enabled – kept for backward compat
      // toggleChromaKey: () => console.log("Chroma key is always enabled"),
      // setChromaKey: () => console.log("Chroma key is always enabled"),
      // isChromaKeyEnabled: () => true,

      getError: () => error,
    }));

    flushPendingSpeakRef.current = async () => {
      if (!pendingSpeakRef.current || !isLiteMode) return;
      if (sessionStateRef.current !== "connected") return;

      const text = pendingSpeakRef.current;
      pendingSpeakRef.current = null;

      try {
        const audioData = await synthesizeTextToPcm(text);
        await sendPcmAudioBuffer(audioData);
        setIsMuted(false);
      } catch (err) {
        console.error("[DEBUG] queued speak error", err);
        setError(err?.message || "Failed to synthesize speech");
      }
    };

    // ---------------------------------------------------------------------------
    // Render (unchanged from original)
    // ---------------------------------------------------------------------------
    const videoAndCanvas = (
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          backgroundImage: "url('/images/ai-generated-office-background.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          margin: "0 auto",
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{
            background: "transparent",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "20px",
            display: "block",
            opacity: isLiteMode ? 0 : 1,
            position: "absolute",
            top: 0,
            left: 0,
            pointerEvents: "none",
          }}
        />
        <audio
          ref={audioRef}
          autoPlay
          playsInline
          style={{ display: "none" }}
        />
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "20px",
            display: isLiteMode ? "block" : "none",
          }}
        />
      </div>
    );

    return (
      <div
        className="flex flex-col items-center space-y-4"
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          background: "#ffffff",
        }}
      >
        {videoAndCanvas}

        {/* Tap-to-enable-audio banner – shown when browser blocks unmuted autoplay */}
        {audioBlocked && (
          <div
            onClick={unlockAudioOnGesture}
            style={{
              position: "absolute",
              top: "12px",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "rgba(0,0,0,0.72)",
              color: "#fff",
              borderRadius: "999px",
              padding: "8px 20px",
              fontSize: "14px",
              cursor: "pointer",
              zIndex: 30,
              whiteSpace: "nowrap",
              userSelect: "none",
            }}
          >
            Tap anywhere to enable audio 🔊
          </div>
        )}

        {/* Mute / unmute button */}
        {connected && !sessionExpired && (
          <div
            onClick={() => {
              if (isMuted) {
                setIsMuted(false);
              } else {
                if (isLiteMode) {
                  sendLiteEvent({ type: "agent.interrupt" });
                } else if (liveAvatarSession) {
                  liveAvatarSession.sendEvent("avatar.interrupt");
                }
                setIsMuted(true);
              }
            }}
            style={{
              position: "absolute",
              bottom: "1vh",
              left: "1vw",
              width: "min(4vw, 40px)",
              height: "min(4vw, 40px)",
              backgroundColor: isMuted
                ? "rgba(220, 53, 69, 0.8)"
                : "rgba(0, 0, 0, 0.6)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 20,
              transition: "all 0.3s ease",
              border: isSpeaking
                ? "min(0.5vw, 3px) solid #28a745"
                : "min(0.5vw, 3px) solid transparent",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isMuted
                ? "rgba(220, 53, 69, 1)"
                : "rgba(0, 0, 0, 0.8)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isMuted
                ? "rgba(220, 53, 69, 0.8)"
                : "rgba(0, 0, 0, 0.6)";
            }}
            title={isMuted ? "Unmute avatar" : "Mute avatar"}
          >
            <div
              style={{
                position: "relative",
                width: "min(5vw, 30px)",
                height: "min(5vw, 30px)",
              }}
            >
              <img
                src="/images/icon_mute_d.svg"
                alt="speaker"
                style={{
                  width: "min(4vw, 24px)",
                  height: "min(4vw, 24px)",
                  opacity: isMuted ? 0.5 : 1,
                }}
              />
              {isMuted && (
                <img
                  src="/images/icon_bar.svg"
                  alt="mute bar"
                  style={{
                    position: "absolute",
                    top: "0",
                    left: "0",
                    width: "min(5.5vw, 32px)",
                    height: "min(5.5vw, 32px)",
                    transform: "scale(1.2)",
                  }}
                />
              )}
            </div>
          </div>
        )}

        {/* Session expired / restart button */}
        {sessionExpired && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "transparent",
              textAlign: "center",
              zIndex: 15,
            }}
          >
            <Lottie
              animationData={powerButtonAnimation}
              style={{
                width: "min(20vw, 120px)",
                height: "min(20vw, 120px)",
                cursor: isInitializing ? "not-allowed" : "pointer",
                opacity: isInitializing ? 0.5 : 1,
              }}
              loop={true}
              onClick={isInitializing ? undefined : restartSession}
            />
            {isInitializing && (
              <p
                style={{
                  color: "white",
                  background: "rgba(0,0,0,0.7)",
                  padding: "min(1vw, 5px) min(2vw, 10px)",
                  borderRadius: "min(1vw, 5px)",
                  marginTop: "min(2vw, 10px)",
                  fontSize: "min(3vw, 14px)",
                }}
              >
                Restarting…
              </p>
            )}
          </div>
        )}
      </div>
    );
  },
);

export default LiveAvatarClient;
